<template>
  <div class="flex flex-col">
    <div class="font-bold p-2 flex">Categories</div>
    <SMList v-if="showMenu" class="p-2">
      <SMListItem
        v-for="category in currentCategories"
        :key="category.id"
        :class="{ selected: category.id === selectedCategoryId }"
        class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border"
        @click.stop="selectCategory(category)"
      >
        <div class="color-button" :style="{ 'background-color': category.color }" />
        <div>
          <span class="category-name" :title="category.name">{{ category.name }}</span>
          &nbsp;
          <span>({{ category.price }})</span>
        </div>
      </SMListItem>

      <SMListItem
        v-for="category in notCurrentCategories"
        :key="category.id"
        :class="{ selected: category.id === selectedCategoryId }"
        class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border"
        @click.stop="selectCategory(category)"
      >
        <div class="color-button" :style="{ 'background-color': category.color }" />
        <div class="md-list-text-container">
          <span class="category-name" :title="category.name">
            {{ category.name }}
          </span>
          &nbsp;
          <span>({{ category.price }})</span>
        </div>
      </SMListItem>
      <SMListItem v-if="!sortedCategories.length" class="flex gap-2">
        <SMIcon icon="warning" />
        <span>
          No categories have been defined.
          <br />
          Please create some first.
        </span>
      </SMListItem>
    </SMList>

    <button
      v-if="categories.length > 0"
      class="flex gap-2 items-center m-2 border rounded-sm p-2 hover:bg-gray-100 cursor-pointer bg-white"
      @click="clearAllCategories"
    >
      <img src="@/assets/img/deleteall.png" alt="Delete" width="32px" height="32px" />
      <span>Clear all categories</span>
    </button>
  </div>
</template>

<script setup>
import SMIcon from '@/components/ui/SMIcon.vue';
import { SEATSTATUS, TYPE } from '@/constants';
import Application from '@/core/application';
import { useState } from '@/store/useStore';
import orderBy from 'lodash/orderBy';
import eventbus from 'tiny-emitter/instance';
import { computed, ref, watch } from 'vue';

const props = defineProps(['selection']);

const app = Application.instance;
app.refreshCategoryList();

const selectedCategoryId = ref();
const currentCategoryIds = ref([]);

const { categories } = useState('categories');

const sortedCategories = computed(() => {
  let sortedCategories = orderBy(categories.value, 'id', 'asc');

  if (!props.selection.items.length || props.selection.items[0].type !== TYPE.GA) return sortedCategories;

  const currentGa = props.selection.items[0];
  let categoryIds = app.seatmap.gaList
    .map((ga) => ga.categoryId)
    .filter((c) => c !== currentGa.categoryId)
    .filter(Boolean);

  return sortedCategories.filter((cat) => !categoryIds.includes(cat.id));
});

const currentCategories = computed(() => {
  return sortedCategories.value.filter((cat) => currentCategoryIds.value.includes(cat.id));
});

const notCurrentCategories = computed(() => {
  return sortedCategories.value.filter((cat) => !currentCategoryIds.value.includes(cat.id));
});

const isMixedSelection = computed(() => {
  return (
    props.selection.items.length > 1 &&
    props.selection.items.some((s) => s.type === TYPE.GA) &&
    (props.selection.items.some((s) => s.type === TYPE.SEAT) ||
      props.selection.items.some((s) => s.type === TYPE.TABLESEAT) ||
      props.selection.items.some((s) => s.type === TYPE.TABLE))
  );
});

const selectionHasNonSeatNonGa = computed(() => {
  return props.selection.items.some((item) => {
    return ![TYPE.GA, TYPE.TABLE, TYPE.SEAT, TYPE.TABLESEAT].includes(item.type);
  });
});

const isBooked = (unit) => {
  return (
    unit.attributes.status === SEATSTATUS.OCCUPIED ||
    unit.attributes.status === SEATSTATUS.RESERVED ||
    unit.attributes.status === SEATSTATUS.CART
  );
};

const hasBookedSeatsInSelection = computed(() => {
  if (!props.selection.items.length) return false;

  return props.selection.items.some((u) => {
    // return [TYPE.SEAT, TYPE.TABLESEAT].includes(u.type) ? isBooked(u) : u.seats.some(isBooked);
    if (u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT) {
      return isBooked(u);
    } else if (u.type === TYPE.TABLE) {
      return u.seats.some(isBooked);
    }
  });
});

const showMenu = computed(() => {
  return !selectionHasNonSeatNonGa.value && !isMixedSelection.value && !hasBookedSeatsInSelection.value;
});

const clearAllCategories = () => {
  props.selection.items.forEach((item) => {
    if (item.type !== TYPE.TABLE) {
      item.category = null;
      item.pos = [];
    } else {
      item.seats.forEach((u) => {
        u.category = null;
        item.pos = [];
      });
    }
  });
  selectedCategoryId.value = null;
  eventbus.emit('seatmap:updated', app.seatmap.seats, app.seatmap.units, app.seatmap.sections);
  eventbus.emit('seatmap:categories:updated');
  eventbus.emit('history:snapshot', 'Cleared category assignment');
};

const selectCategory = (category) => {
  selectedCategoryId.value = category.id;
  eventbus.emit('category:set', selectedCategoryId.value);
  eventbus.emit('seatmap:updated', app.seatmap.seats, app.seatmap.units, app.seatmap.sections);
  eventbus.emit('history:snapshot', `Category assigned: ${category.name}`);
};

watch(
  () => props.selection,
  () => {
    if (selectionHasNonSeatNonGa.value) return;

    const ids = new Set();
    props.selection.items.forEach((item) => {
      switch (item.type) {
        case TYPE.SEAT:
        case TYPE.TABLESEAT:
          ids.add(item.categoryId);
          break;
        case TYPE.TABLE:
          ids.add(item.seats[0].categoryId);
          break;
        default:
          ids.add(item.categoryId);
      }
    });

    currentCategoryIds.value = [...ids];
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.md-list-text-container {
  display: flex !important;
  flex-direction: row;
  flex-flow: row;
  justify-content: flex-start;
  width: 200px;
  > span {
    align-self: center;
  }
  .align-right {
    margin-left: auto;
  }
}

.triangle {
  font-size: 12px;
  position: absolute;
  right: -12px;
}

.category-name {
  max-width: 200px;
  margin-right: 3px;
}

.vc-chrome {
  position: absolute !important;
  top: -1px;
  left: -226px;
  z-index: 8;
}

.md-avatar {
  overflow: visible !important;
}
.color-button {
  min-width: 32px;
  min-height: 32px;
}

.pading-left {
  padding-left: 5px;
}

.no-padding {
  padding: 0 !important;
}

.selected {
  background-color: #4051b4 !important;
  span {
    color: white !important;
  }
}

.md-list-item:not(.sub-list-container) {
  padding: 2px 0;
  border-bottom: 1px solid #eae8e8;
  &:first-child {
    padding-top: 2px;
  }
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: #eaeaea;
  }
}

.md-list.md-dense {
  padding: 0 0 !important;
}
.category-icon {
  width: 20px;
  &.clickable {
    opacity: 0.4;
    &:hover {
      opacity: 1;
    }
  }
}
.sub-list-container .md-divider {
  bottom: auto !important;
}
.md-input-container {
  min-height: auto !important;
  margin: 0 !important;
  padding-top: 0;
  width: 165px;
  display: inline-block !important;
  vertical-align: middle;
}
</style>
